import { Formik } from 'formik'
import { debounce, isEqual, map } from 'lodash'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { defaultMemoize } from 'reselect'

import Card from '@/components/Card'
import DeleteConfirmationOverlay from '@/components/DeleteConfirmationOverlay'
import { AutoComplete } from '@/components/Form'
import { Person } from '@/components/Icons'
import PaginationTable from '@/components/PaginationTable'
import { FETCH_ORG_USERS_TO_ADD } from '@/pages/OrganizationSettingsPage/actionTypes'

import columns from './columns'
import messages from './messages'

import './index.scss'

import { usePrevious } from 'react-use'

import { USER_ATTRIBUTES } from '@/components/User'

/**
 * Prevent unnecessary network requests to fetch resolved targeting rules and attribute suggestions by waiting for
 * further user input for this amount of time before fetching. This technique is called debouncing
 *
 * @type {number} time to delay in milliseconds
 */
const SEARCH_DELAY_MS = 300

function OrganizationAdmins(props: any) {
  const intl = useIntl()

  const [state, _setState] = useState({
    currentPage: 1,
    searchTerm: '',
    searchResults: [] as any[],
    userToRemove: undefined as { id: any } | undefined,
    isDeleting: undefined as boolean | undefined
  })
  const setState = (update: Partial<Parameters<typeof _setState>[0]>) => {
    return _setState(state => ({ ...state, ...(typeof update === 'function' ? update(state) : update) }))
  }

  // componentDidUpdate
  const prevProps = usePrevious(props)
  useEffect(() => {
    if (!prevProps) return

    // Compare both the data and the overall status.
    // We want to remove results from the dropdown when the request is in progress
    if (
      (props.isSearchUsersComplete && !prevProps.isSearchUsersComplete) ||
      !isEqual(props.searchResults, prevProps.searchResults)
    ) {
      setState({
        searchResults: props.searchResults
      })
    }
  }, [prevProps, props])

  const addOrganizationAdminRole = async (user: any) => {
    const { addOrganizationAdmin } = props

    addOrganizationAdmin(user.id)
  }

  const fetchUserSearchResults = debounce(filter => {
    const query: { attributes: string[]; count: number; filter?: string } = {
      attributes: USER_ATTRIBUTES,
      count: 10
    }

    if (filter) {
      query.filter = ['userName', 'name.formatted', 'name.familyName', 'name.givenName', 'displayName']
        .map(attribute => `${attribute} co ${JSON.stringify(filter)}`)
        .join(' or ')
    }

    props.fetchUsersPaginated(query, FETCH_ORG_USERS_TO_ADD)
  }, SEARCH_DELAY_MS)

  const generateSearchResults = defaultMemoize((searchTerm, users) => {
    if (searchTerm.length === 0) {
      return []
    }

    return map(users, user => ({
      label: user.displayName,
      leftIcon: <Person />,
      subLabel: user.email,
      value: user.id,
      id: user.id
    }))
  })

  const getActionMessage = () => {
    if (props.isAddingOrganizationAdmin) {
      return <FormattedMessage {...messages.addingRole} />
    } else if (props.isRemovingOrganizationAdmin) {
      return <FormattedMessage {...messages.removingRole} />
    }
  }

  const onSearchChange = (value: any) => {
    value = value.trim()

    setState({ searchTerm: value })

    // Don't search if the user enters in spaces
    if (value.length === 0) {
      return
    }

    return fetchUserSearchResults(value)
  }

  const onSearchSelect = (value: any) => {
    // Reset the input to the search box
    setState({ searchTerm: '' })

    addOrganizationAdminRole(state.searchResults.find(user => user.id === value.id))

    return { display: '', value: '' }
  }

  const renderItem = ({ label, leftIcon, subLabel, value }: any) => {
    return (
      <div key={value} className="organization-member-result">
        {!!leftIcon && <div className="result__left">{leftIcon}</div>}
        <div className="result__right">
          <p>{label}</p>
          {!!subLabel && <p>{subLabel}</p>}
        </div>
      </div>
    )
  }

  const removeOrganizationAdminRole = () => {
    const { userToRemove } = state

    setState({
      isDeleting: false
    })

    try {
      userToRemove && props.removeOrganizationAdmin(userToRemove.id)
    } finally {
      setState({
        userToRemove: null
      })
    }
  }

  const showConfirmDelete = (user: any) => {
    setState({ isDeleting: true, userToRemove: user })
  }

  const onCancelDelete = () => {
    setState({ isDeleting: false, userToRemove: '' })
  }

  const onSelect = (page: any) => {
    setState({ currentPage: page })
  }

  return (
    <Card
      isActionInProgress={
        props.isAddingOrganizationAdmin || props.isFetchingAdmins || props.isRemovingOrganizationAdmin
      }
      actionMessage={getActionMessage()}
    >
      <Card.Header title={<FormattedMessage {...messages.organizationAdministrators} />} />

      <Card.Content>
        {/* @ts-expect-error */}
        <Formik initialValues={{ search: state.searchTerm }}>
          <AutoComplete
            className="admin-search_autocomplete"
            key="organizationAdmin"
            name="organizationAdmin"
            label={<FormattedMessage {...messages.addAdministrator} />}
            placeholder={intl.formatMessage(messages.searchPlaceholder)}
            onResultClick={onSearchSelect}
            onSearchChange={onSearchChange}
            renderResult={renderItem}
            isSearching={props.isSearching}
            results={generateSearchResults(state.searchTerm, state.searchResults)}
          />
        </Formik>

        {props.organizationAdmins.length > 0 && (
          <PaginationTable
            columns={columns({
              intl: intl,
              showConfirmDelete: showConfirmDelete,
              currentUser: props.currentUser
            })}
            pagination={{
              isClientPaging: true,
              startIndex: state.currentPage * 10,
              count: props.organizationAdmins.length,
              perPage: 10
            }}
            onPaginateSelect={onSelect}
            rows={props.organizationAdmins}
            rowItemType={<FormattedMessage {...messages.users} />}
            rowKey="id"
            sortingColumn={props.sortingColumn}
          />
        )}

        {props.organizationAdmins <= 0 && !props.isAddingOrganizationAdmin && (
          <p className="no-users flex-center">
            <FormattedMessage {...messages.noAdmins} />
          </p>
        )}
      </Card.Content>

      {!!state.isDeleting && (
        <DeleteConfirmationOverlay
          onConfirm={removeOrganizationAdminRole}
          onCancel={onCancelDelete}
          message={<FormattedMessage {...messages.confirmRemove} />}
          subMessage={<FormattedMessage {...messages.confirmOrganizationAdminRemove} />}
        />
      )}
    </Card>
  )
}

OrganizationAdmins.displayName = 'OrganizationAdmins'

OrganizationAdmins.propTypes = {
  addOrganizationAdmin: PropTypes.func.isRequired,
  currentUser: PropTypes.object,
  fetchUsersPaginated: PropTypes.func.isRequired,

  isSearching: PropTypes.bool.isRequired,
  isFetchingAdmins: PropTypes.bool.isRequired,
  isAddingOrganizationAdmin: PropTypes.bool.isRequired,
  isRemovingOrganizationAdmin: PropTypes.bool.isRequired,
  isSearchUsersComplete: PropTypes.bool.isRequired,
  organizationAdmins: PropTypes.arrayOf(PropTypes.object),
  removeOrganizationAdmin: PropTypes.func.isRequired,
  searchResults: PropTypes.arrayOf(PropTypes.object),
  sortingColumn: PropTypes.object
}

OrganizationAdmins.defaultProps = {
  currentUser: {},
  searchResults: null,
  organizationAdmins: [],
  sortingColumn: { name: { direction: 'asc', position: 1 } }
}

export default OrganizationAdmins
